import { isNumber } from 'lodash';
import { create } from 'zustand';
import { IExportState, StateType } from '@/version-types';
import { CoreAPI, getState, useState } from '../../core';
import { IGlobalMask } from '../type';
import { getLayerOperateState } from './layer-operate-state';

/** 状态 */
interface IState {
    /** 是否禁用编辑器操作 */
    disableOperate: boolean;
    /** 触发禁用图层选择的操作 */
    disablePick: boolean;
    /** 是否正在执行鼠标框选 */
    disableMouseSelect: boolean;
    /** 是否禁用移动 */
    disableMove: boolean;
    /** 是否禁用缩放 */
    disableScale: boolean;
    /** 是否禁用旋转 */
    disableRotate: boolean;
    /** 是否禁用尺寸调整 */
    disableResize: boolean;
    /** 是否正在执行基础操作：移动，旋转，缩放，尺寸 */
    isBaseOperate: boolean;
    /** 是否正在执行鼠标框选 */
    isMouseSelect: boolean;
    /** 是否禁用激活图层 */
    disableActive: boolean;
    /** 光标提示语版本 */
    cursorTipVer: number;
    /** 全局遮罩 */
    globalMask: IGlobalMask | null;
    /** 是否禁用 Hover */
    disableHover: boolean;
}

/** 动作 */
interface IAction {
    /** 设置是否禁用编辑器操作 */
    setDisableOperate: (disableOperate: boolean, delActiveLayer?: boolean) => void;
    /** 设置触发禁用图层选择的操作 */
    setDisablePick: (disablePick: boolean, delActiveLayer?: boolean) => void;
    /** 设置是否正在执行基础操作 */
    setIsBaseOperate: (isBaseOperate: boolean) => void;
    /** 设置是否正在执行鼠标框选 */
    setDisableMouseSelect: (disableMouseSelect: boolean) => void;
    /** 设置是否禁用移动 */
    setDisableMove: (disableMove: boolean) => void;
    /** 设置是否禁用缩放 */
    setDisableScale: (disableScale: boolean) => void;
    /** 设置是否禁用旋转 */
    setDisableRotate: (disableRotate: boolean) => void;
    /** 设置是否禁用尺寸调整 */
    setDisableResize: (disableResize: boolean) => void;
    /** 设置是否正在执行鼠标框选 */
    setIsMouseSelect: (isMouseSelect: boolean) => void;
    /** 设置是否禁用激活图层 */
    setDisableActive: (disableActive: boolean) => void;
    /** 增加光标提示版本 */
    setCursorTipVer: (version?: number) => void;
    /** 设置全局遮罩 */
    setGlobalMask: (globalMask: IGlobalMask | null) => void;
    /** 设置是否禁用 Hover */
    setDisableHover: (disableHover: boolean) => void;
    /** 销毁 */
    destroy: () => void;
}

/** 存储类型 */
interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState() {
    return {
        disableOperate: false,
        disablePick: false,
        disableMouseSelect: false,
        disableMove: false,
        disableScale: false,
        disableRotate: false,
        disableResize: false,
        isBaseOperate: false,
        isMouseSelect: false,
        disableActive: false,
        disableHover: false,
        cursorTipVer: 0,
        globalMask: null,
    };
}

function emitEditorOperateState(identity: string, get: () => IStore) {
    CoreAPI(identity).events.emitEditorOperateState({
        disableOperate: get().disableOperate,
        disablePick: get().disablePick,
        disableMouseSelect: get().disableMouseSelect,
        disableMove: get().disableMove,
        disableScale: get().disableScale,
        disableRotate: get().disableRotate,
        disableResize: get().disableResize,
        isBaseOperate: get().isBaseOperate,
        isMouseSelect: get().isMouseSelect,
        disableActive: get().disableActive,
    });
}

/** 创建存储 */
const createState = (identity: string) => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),

        destroy() {
            set(getInitialState());
            emitEditorOperateState(identity, get);
        },

        setDisableOperate(disableOperate, delActiveLayer) {
            if (disableOperate && delActiveLayer) {
                getLayerOperateState(identity).delActiveLayer();
            }
            set({ disableOperate });
            emitEditorOperateState(identity, get);
        },

        setDisablePick(disablePick, delActiveLayer = true) {
            if (disablePick && delActiveLayer) {
                getLayerOperateState(identity).delActiveLayer();
            }
            set({ disablePick });
            emitEditorOperateState(identity, get);
        },

        setIsBaseOperate(isBaseOperate) {
            get().setDisablePick(isBaseOperate, false);
            if (get().isBaseOperate !== isBaseOperate) {
                set({ isBaseOperate });
                CoreAPI(identity).events.emitBaseOperateChange(isBaseOperate);
                emitEditorOperateState(identity, get);
            }
        },

        setDisableMouseSelect(disableMouseSelect) {
            set({ disableMouseSelect });
            emitEditorOperateState(identity, get);
        },

        setIsMouseSelect(isMouseSelect) {
            if (get().isMouseSelect !== isMouseSelect) {
                set({ isMouseSelect });
                emitEditorOperateState(identity, get);
            }
        },

        setDisableActive(disableActive) {
            set({ disableActive });
            emitEditorOperateState(identity, get);
        },

        setDisableMove(disableMove) {
            set({ disableMove });
            emitEditorOperateState(identity, get);
        },

        setDisableScale(disableScale) {
            set({ disableScale });
            emitEditorOperateState(identity, get);
        },

        setDisableRotate(disableRotate) {
            set({ disableRotate });
            emitEditorOperateState(identity, get);
        },

        setDisableResize(disableResize) {
            set({ disableResize });
            emitEditorOperateState(identity, get);
        },

        setCursorTipVer(version) {
            if (isNumber(version)) {
                set({ cursorTipVer: version });
            } else {
                set((state) => ({ cursorTipVer: state.cursorTipVer + 1 }));
            }
        },

        setGlobalMask(globalMask) {
            set({ globalMask });
        },

        setDisableHover(disableHover) {
            set({ disableHover });
            emitEditorOperateState(identity, get);
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'editor-operate-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useEditorOperateState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getEditorOperateState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
